// Observações livres do processo (cada uma com data), na coluna lateral da página do processo
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Check, Loader2, Pencil, StickyNote, Trash2, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Note {
  id: string;
  content: string;
  created_at: string;
  updated_at: string;
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export const CaseNotes = ({ caseId }: { caseId: string }) => {
  const { toast } = useToast();
  const [notes, setNotes] = useState<Note[]>([]);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  const load = async () => {
    const { data } = await (supabase as any)
      .from('case_notes')
      .select('id, content, created_at, updated_at')
      .eq('case_id', caseId)
      .order('created_at', { ascending: false });
    setNotes((data as Note[]) || []);
  };

  useEffect(() => {
    load();
  }, [caseId]);

  const add = async () => {
    const content = draft.trim();
    if (!content) return;
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Sessão expirada. Entre novamente.');
      const { error } = await (supabase as any).from('case_notes').insert({ case_id: caseId, user_id: user.id, content });
      if (error) throw error;
      setDraft('');
      load();
    } catch (error: any) {
      toast({ title: 'Não foi possível salvar', description: error?.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (id: string) => {
    const content = editText.trim();
    if (!content) return;
    const { error } = await (supabase as any)
      .from('case_notes')
      .update({ content, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) {
      toast({ title: 'Não foi possível salvar', description: error.message, variant: 'destructive' });
      return;
    }
    setEditingId(null);
    load();
  };

  const remove = async (id: string) => {
    if (!window.confirm('Excluir esta observação?')) return;
    const { error } = await (supabase as any).from('case_notes').delete().eq('id', id);
    if (error) {
      toast({ title: 'Não foi possível excluir', description: error.message, variant: 'destructive' });
      return;
    }
    load();
  };

  return (
    <section className="rounded-2xl border border-slate-200/80 bg-white p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Anotações</p>
          <p className="mt-0.5 text-lg font-semibold tracking-tight text-slate-900">Observações</p>
        </div>
        <StickyNote className="h-5 w-5 text-[#c4ab83]" />
      </div>

      <div className="mt-3 space-y-2">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) add();
          }}
          placeholder="Escreva uma observação sobre o processo..."
          rows={3}
          className="resize-none rounded-xl border-slate-200 text-sm shadow-none placeholder:text-slate-400 focus-visible:ring-[#c4ab83]/30"
        />
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-slate-400">Ctrl + Enter para salvar</span>
          <Button size="sm" onClick={add} disabled={saving || !draft.trim()} className="h-8 rounded-lg bg-slate-900 px-3 text-[12px] text-white hover:bg-slate-800">
            {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1.5 h-3.5 w-3.5 text-[#e8d5a8]" />}
            Salvar
          </Button>
        </div>
      </div>

      {notes.length > 0 && (
        <ul className="mt-4 max-h-96 space-y-2 overflow-y-auto">
          {notes.map((note) => (
            <li key={note.id} className="group rounded-xl bg-[#faf7f2] px-3 py-2.5">
              {editingId === note.id ? (
                <div className="space-y-2">
                  <Textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    rows={3}
                    autoFocus
                    className="resize-none rounded-lg border-slate-200 bg-white text-sm shadow-none focus-visible:ring-[#c4ab83]/30"
                  />
                  <div className="flex justify-end gap-1">
                    <button type="button" onClick={() => setEditingId(null)} className="rounded-md p-1 text-slate-400 hover:bg-white hover:text-slate-700" title="Cancelar">
                      <X className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => saveEdit(note.id)} className="rounded-md p-1 text-emerald-600 hover:bg-white" title="Salvar">
                      <Check className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{note.content}</p>
                  <div className="mt-1.5 flex items-center justify-between">
                    <span className="text-[11px] text-slate-400">
                      {formatDate(note.created_at)}
                      {note.updated_at && new Date(note.updated_at).getTime() - new Date(note.created_at).getTime() > 60000 && ' · editada'}
                    </span>
                    <span className="flex gap-0.5 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingId(note.id);
                          setEditText(note.content);
                        }}
                        className="rounded-md p-1 text-slate-400 hover:bg-white hover:text-slate-700"
                        title="Editar"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button type="button" onClick={() => remove(note.id)} className="rounded-md p-1 text-slate-400 hover:bg-white hover:text-red-600" title="Excluir">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
